/**
 * Theme Manager Utility
 * 
 * Provides runtime theme switching capabilities and localStorage persistence
 */

import { 
  ACTIVE_THEME,
  getAvailableThemes, 
  getThemeByName, 
  applyTheme,
  type ThemeConfig 
} from '../config/theme';
import { gtagService } from '../services/gtagService';

const THEME_STORAGE_KEY = 'democrackers-theme';

export class ThemeManager {
  private static instance: ThemeManager;
  private currentTheme: ThemeConfig;
  private listeners: Array<(theme: ThemeConfig) => void> = [];

  private constructor() {
    this.currentTheme = this.loadThemeFromStorage() || ACTIVE_THEME;
    this.applyCurrentTheme();
  }

  public static getInstance(): ThemeManager {
    if (!ThemeManager.instance) {
      ThemeManager.instance = new ThemeManager();
    }
    return ThemeManager.instance;
  }

  /**
   * Get current active theme
   */
  public getCurrentTheme(): ThemeConfig {
    return this.currentTheme;
  }

  /**
   * Get all available themes
   */
  public getAvailableThemes(): ThemeConfig[] {
    return getAvailableThemes();
  }

  /**
   * Switch to a new theme
   */
  public switchTheme(themeName: string): boolean {
    const newTheme = getThemeByName(themeName);
    if (!newTheme) {
      console.warn(`Theme "${themeName}" not found`);
      return false;
    }

    this.currentTheme = newTheme;
    this.applyCurrentTheme();
    this.saveThemeToStorage();
    this.notifyListeners();
    
    // Track theme change
    gtagService.trackThemeChange(newTheme.name);
    
    return true;
  }

  /**
   * Switch to theme by config object
   */
  public setTheme(theme: ThemeConfig): void {
    this.currentTheme = theme;
    this.applyCurrentTheme();
    this.saveThemeToStorage();
    this.notifyListeners();
  }

  /**
   * Reset to default theme
   */
  public resetToDefault(): void {
    this.currentTheme = ACTIVE_THEME;
    this.applyCurrentTheme();
    this.saveThemeToStorage();
    this.notifyListeners();
  }

  /**
   * Subscribe to theme changes
   */
  public subscribe(callback: (theme: ThemeConfig) => void): () => void {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Check if theme is currently active
   */
  public isThemeActive(themeName: string): boolean {
    return this.currentTheme.name === themeName;
  }

  /**
   * Get theme preview data for UI
   */
  public getThemePreview(themeName: string): { 
    name: string; 
    description: string; 
    colors: { primary: string; accent: string; success: string } 
  } | null {
    const theme = getThemeByName(themeName);
    if (!theme) return null;

    return {
      name: theme.name,
      description: theme.description,
      colors: {
        primary: theme.primary[500],
        accent: theme.accent[500],
        success: theme.success[500] || '#22c55e'
      }
    };
  }

  private applyCurrentTheme(): void {
    applyTheme(this.currentTheme);
  }

  private saveThemeToStorage(): void {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, this.currentTheme.name);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }

  private loadThemeFromStorage(): ThemeConfig | null {
    try {
      const savedThemeName = localStorage.getItem(THEME_STORAGE_KEY);
      if (savedThemeName) {
        return getThemeByName(savedThemeName) || null;
      }
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
    }
    return null;
  }

  private notifyListeners(): void {
    this.listeners.forEach(callback => {
      try {
        callback(this.currentTheme);
      } catch (error) {
        console.error('Theme listener error:', error);
      }
    });
  }
}

// Export singleton instance
export const themeManager = ThemeManager.getInstance();

// Export hook for React components
export const useTheme = () => {
  const manager = ThemeManager.getInstance();
  
  return {
    currentTheme: manager.getCurrentTheme(),
    availableThemes: manager.getAvailableThemes(),
    switchTheme: (name: string) => manager.switchTheme(name),
    isThemeActive: (name: string) => manager.isThemeActive(name),
    getThemePreview: (name: string) => manager.getThemePreview(name),
    subscribe: (callback: (theme: ThemeConfig) => void) => manager.subscribe(callback),
    resetToDefault: () => manager.resetToDefault(),
  };
};